import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Twitter, Facebook, Linkedin, MessageSquare, Mail, Copy, Check,
} from 'lucide-react';
import { toast } from 'sonner';

/**
 * CaseShareBar — a simple 5-button "help identify" share row.
 *
 * Differences from the existing <ShareButtons>:
 *   - Uses the BRANDED short-share URL `linkthemissing.com/api/c/{id}` so
 *     previews flow through the backend SSR route and pick up per-case
 *     OG meta tags, including for crawlers that don't run JS (Twitter,
 *     Slack, iMessage).
 *   - Fixed CTA copy: "Help identify this person — <case name>".
 *   - One click per platform — no dropdown.
 *   - Tracks each share via the existing /api/cases/{id}/share endpoint
 *     so the main share-count stays consistent.
 */
export default function CaseShareBar({
  caseData, caseId,
  platforms,          // optional: subset of ['x','facebook','linkedin','sms','email']
  title,              // optional: override header title
  subtitle,           // optional: override subtitle text
  cta: ctaOverride,   // optional: override CTA / share-copy text
  tone = 'neutral',   // 'neutral' (default blue/slate) | 'resolved' (emerald) | 'progression' (purple)
  progression = false, // when true: route share through ?ap=1 so the OG card uses the AI projection
}) {
  const [copied, setCopied] = useState(false);

  const id = caseId || caseData?.id;
  // NOTE: We deliberately use `/api/c/{id}` (not `/c/{id}`) for the share URL.
  // Emergent's ingress only forwards `/api/*` paths to the FastAPI backend;
  // bare `/c/*` is intercepted by the frontend nginx and serves the static
  // SPA shell, which means social crawlers never reach the per-case SSR
  // route in `routes/short_share.py` and end up caching the generic
  // homepage OG card. Routing through `/api/c/{id}` lets crawlers see the
  // per-case meta tags, while humans still get an identical UX because the
  // route 302-redirects browsers back to `/case/{id}`.
  // When `progression` is true we tack on `?ap=1` so the SSR route swaps
  // the OG image for the AI age-projection variant.
  const shortUrl = progression
    ? `https://linkthemissing.com/api/c/${id}?ap=1`
    : `https://linkthemissing.com/api/c/${id}`;

  // Build the case-name slug used in the CTA copy.
  const displayName = (() => {
    const n = (caseData?.name || '').trim();
    if (n && !n.toLowerCase().startsWith('unidentified')) {
      return n.replace(/^[:\s]+/, '');
    }
    const sex = caseData?.sex || caseData?.gender || 'Person';
    const amin = caseData?.age_min, amax = caseData?.age_max;
    if (amin && amax && amin !== amax) return `Unidentified ${sex}, age ${amin}–${amax}`;
    if (amin) return `Unidentified ${sex}, age ${amin}`;
    if (caseData?.age) return `Unidentified ${sex}, age ${caseData.age}`;
    return `Unidentified ${sex}`;
  })();

  const cta = ctaOverride || (
    progression
      ? `AI age projection — help recognize ${displayName}`
      : `Help identify this person — ${displayName}`
  );

  const track = async (platform) => {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    try {
      await fetch(`${API}/cases/${id}/share?platform=${platform}`, {
        method: 'POST',
      });
    } catch {
      /* best-effort tracking */
    }
  };

  const handleX = async () => {
    await track('twitter');
    const u = `https://twitter.com/intent/tweet?text=${encodeURIComponent(cta)}&url=${encodeURIComponent(shortUrl)}`;
    window.open(u, '_blank', 'width=600,height=500');
  };

  const handleFacebook = async () => {
    await track('facebook');
    const u = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shortUrl)}&quote=${encodeURIComponent(cta)}`;
    window.open(u, '_blank', 'width=600,height=500');
  };

  const handleLinkedIn = async () => {
    await track('linkedin');
    const u = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(shortUrl)}`;
    window.open(u, '_blank', 'width=600,height=500');
  };

  const handleSms = async () => {
    await track('sms');
    // sms: URI is universal on iOS and Android; the ?body= param is
    // technically iOS-specific but Android supports it too via the
    // Messages app.
    const body = encodeURIComponent(`${cta}\n\n${shortUrl}`);
    // iOS prefers the `&body=` form with a recipient separator, Android
    // is happy with `?body=`. `sms:?body=` works on both in practice.
    window.location.href = `sms:?&body=${body}`;
  };

  const handleEmail = async () => {
    await track('email');
    const subject = encodeURIComponent(cta);
    const body = encodeURIComponent(`${cta}\n\n${shortUrl}`);
    window.location.href = `mailto:?subject=${subject}&body=${body}`;
  };

  const handleCopy = async () => {
    await track('copy');
    try {
      await navigator.clipboard.writeText(`${cta}\n\n${shortUrl}`);
      setCopied(true);
      toast.success('Share link copied', {
        description: shortUrl,
      });
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error('Copy failed — please copy the URL manually');
    }
  };

  const allButtons = [
    { id: 'x',        label: 'X',         icon: Twitter,        onClick: handleX,        tone: 'hover:bg-black hover:text-white hover:border-black' },
    { id: 'facebook', label: 'Facebook',  icon: Facebook,       onClick: handleFacebook, tone: 'hover:bg-[#1877F2] hover:text-white hover:border-[#1877F2]' },
    { id: 'linkedin', label: 'LinkedIn',  icon: Linkedin,       onClick: handleLinkedIn, tone: 'hover:bg-[#0A66C2] hover:text-white hover:border-[#0A66C2]' },
    { id: 'sms',      label: 'SMS',       icon: MessageSquare,  onClick: handleSms,      tone: 'hover:bg-emerald-600 hover:text-white hover:border-emerald-600' },
    { id: 'email',    label: 'Email',     icon: Mail,           onClick: handleEmail,    tone: 'hover:bg-slate-700 hover:text-white hover:border-slate-700' },
  ];
  const buttons = platforms
    ? allButtons.filter((b) => platforms.includes(b.id))
    : allButtons;

  // Pretty short label shown in the UI. The actual share URL stays
  // `/api/c/{id}[?ap=1]` (so social crawlers reach the SSR meta-tag
  // route) but exposing that path in the UI is confusing for families
  // — it looks like an internal API endpoint. We render a clean
  // human-friendly version with the case's display name slugified
  // and tag the AP variant so users know what they're sharing.
  const displaySlug = (displayName || 'case')
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')   // strip accents
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 40) || 'case';
  const prettyLabel = progression
    ? `linkthemissing.com/${displaySlug}/age-progression`
    : `linkthemissing.com/${displaySlug}`;

  const toneCls = tone === 'resolved'
    ? 'border-emerald-200 bg-emerald-50'
    : tone === 'progression'
      ? 'border-purple-200 bg-purple-50 dark:border-purple-900/40 dark:bg-purple-950/20'
      : 'border-slate-200 bg-slate-50';
  const codeCls = tone === 'resolved'
    ? 'bg-white border-emerald-200 text-emerald-900'
    : tone === 'progression'
      ? 'bg-white border-purple-200 text-purple-900'
      : 'bg-white border-slate-200 text-slate-700';

  return (
    <div
      data-testid={`case-share-bar-${id}`}
      className={`rounded-lg border p-4 ${toneCls}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap mb-3">
        <div>
          <div className="text-sm font-semibold text-slate-900">
            {title || 'Help identify this person'}
          </div>
          <div className="text-xs text-slate-600 mt-0.5">
            {subtitle || 'One-click share with branded link preview — '}
            <code
              className={`text-[11px] px-1.5 py-0.5 rounded border ${codeCls}`}
              title={shortUrl}
              data-testid="case-share-pretty-url"
            >
              {prettyLabel}
            </code>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={handleCopy}
          data-testid="case-share-copy-btn"
          className="text-xs">
          {copied
            ? <><Check className="w-3 h-3 mr-1" /> Copied</>
            : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {buttons.map((b) => (
          <Button
            key={b.id}
            variant="outline"
            size="sm"
            onClick={b.onClick}
            data-testid={`case-share-${b.id}-btn`}
            className={`gap-1.5 transition-colors ${b.tone}`}>
            <b.icon className="w-3.5 h-3.5" />
            {b.label}
          </Button>
        ))}
      </div>
    </div>
  );
}
